import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Toasts for anything that leaves the page — a form posted, a link copied, a
 * request that failed. Every outcome gets one, not just the happy path, so a
 * silent failure never reads as success.
 */

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
    id: number;
    tone: ToastTone;
    title: string;
    /** Optional second line — what went wrong, or what happens next. */
    body?: string;
}

type ToastInput = Omit<Toast, 'id'>;

interface ToastApi {
    show: (t: ToastInput) => void;
    success: (title: string, body?: string) => void;
    error: (title: string, body?: string) => void;
    info: (title: string, body?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** How long a toast stays up. Errors linger, because they need reading. */
const LIFETIME: Record<ToastTone, number> = {
    success: 4000,
    info: 4000,
    warning: 6000,
    error: 8000,
};

const TONE_STYLE: Record<ToastTone, { bar: string; icon: string; path: string }> = {
    success: { bar: 'bg-[#1c9c52]', icon: 'text-[#1c9c52]', path: 'M5 13l4 4L19 7' },
    error: { bar: 'bg-[#dd3333]', icon: 'text-[#dd3333]', path: 'M12 8v5m0 3.5v.01M12 3l9 16H3l9-16Z' },
    warning: { bar: 'bg-[#f60]', icon: 'text-[#f60]', path: 'M12 8v5m0 3.5v.01M12 3l9 16H3l9-16Z' },
    info: {
        bar: 'bg-[#2563eb]',
        icon: 'text-[#2563eb]',
        path: 'M12 11v6m0-9.5v.01M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18Z',
    },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const nextId = useRef(1);
    const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    const dismiss = useCallback((id: number) => {
        setToasts((list) => list.filter((t) => t.id !== id));
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
    }, []);

    const show = useCallback(
        (input: ToastInput) => {
            const id = nextId.current++;
            setToasts((list) => [...list, { ...input, id }]);
            timers.current.set(
                id,
                setTimeout(() => dismiss(id), LIFETIME[input.tone]),
            );
        },
        [dismiss],
    );

    // Clear every pending timer if the provider goes away mid-flight.
    useEffect(() => {
        const pending = timers.current;
        return () => pending.forEach(clearTimeout);
    }, []);

    const api = useMemo<ToastApi>(
        () => ({
            show,
            success: (title, body) => show({ tone: 'success', title, body }),
            error: (title, body) => show({ tone: 'error', title, body }),
            info: (title, body) => show({ tone: 'info', title, body }),
        }),
        [show],
    );

    return (
        <ToastContext.Provider value={api}>
            {children}

            <div
                role="region"
                aria-label="Notifications"
                className="pointer-events-none fixed bottom-5 right-5 z-[4000] flex w-[min(360px,calc(100vw-40px))] flex-col gap-3"
            >
                {toasts.map((t) => {
                    const style = TONE_STYLE[t.tone];
                    return (
                        <div
                            key={t.id}
                            role="status"
                            aria-live={t.tone === 'error' ? 'assertive' : 'polite'}
                            className="pointer-events-auto flex animate-[toast-in_260ms_cubic-bezier(0.22,1,0.36,1)] overflow-hidden rounded-[10px] bg-white shadow-[0_18px_44px_-12px_rgba(0,0,0,0.3)] ring-1 ring-black/5"
                        >
                            <span className={`w-[4px] shrink-0 ${style.bar}`} />
                            <div className="flex flex-1 items-start gap-3 p-4">
                                <svg
                                    className={`mt-0.5 size-[18px] shrink-0 ${style.icon}`}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    aria-hidden
                                >
                                    <path
                                        d={style.path}
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[13px] font-bold leading-[1.4] text-[#08080c]">{t.title}</p>
                                    {t.body && (
                                        <p className="mt-1 text-[12px] leading-[1.6] text-[#5e5e5e]">{t.body}</p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => dismiss(t.id)}
                                    aria-label="Dismiss notification"
                                    className="-mr-1 -mt-1 flex size-7 shrink-0 items-center justify-center rounded-full text-[#9a9a9a] transition-colors hover:bg-[#f0f0f0] hover:text-[#08080c]"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <path
                                            d="M6 6l12 12M18 6L6 18"
                                            stroke="currentColor"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
}

/** Throws outside a provider rather than silently doing nothing. */
export function useToast(): ToastApi {
    const api = useContext(ToastContext);
    if (!api) throw new Error('useToast must be used inside a ToastProvider');
    return api;
}
